import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thbShort, periodNow, periodLabel, shortDate } from '../money.js';
import { Card, Kpi, Empty, Spinner, Avatar, EntityGlyph, Chip, PeriodStepper } from '../ui.jsx';

/**
 * Insights — four questions, in the order they get asked in a review:
 * where deals are stalling, who is closing, why we lost, and what is quietly
 * rotting. The gaps half is the one people act on, so it is not buried at the
 * bottom of a wall of charts the way v2's dashboard was.
 */
export default function Insights({ me, onOpenDeal, onOpenClient }) {
  const { t, lang } = useLang();
  const [period, setPeriod] = useState(periodNow);
  const [entity, setEntity] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const mine = me.role === 'admin' ? ['BTM', 'MR'] : me.entities;

  const load = useCallback(() => {
    setData(null);
    setError('');
    api
      .insights({ period, entity })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [period, entity]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="page">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 26 }}>
            {t('navInsights')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 3 }}>
            {t('insightsBlurb')}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {mine.length > 1 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <Chip on={entity === ''} onClick={() => setEntity('')}>
                {t('allEntities')}
              </Chip>
              {mine.map((e) => (
                <Chip key={e} on={entity === e} onClick={() => setEntity(e)}>
                  {e}
                </Chip>
              ))}
            </div>
          )}
          <PeriodStepper period={period} onChange={setPeriod} max={periodNow()} />
        </div>
      </div>

      {error && <div className="page">{error}</div>}
      {!error && !data && <Spinner label={t('loading')} />}

      {data && data.entities.length === 0 && <Empty>{t('noEntityAccess')}</Empty>}

      {data && data.entities.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <Kpi label={t('wonCol')} value={thbShort(data.win_loss.won_satang)} />
            <Kpi label={t('valueLost')} value={thbShort(data.win_loss.lost_satang)} />
            <Kpi
              label={t('winRate')}
              value={
                data.win_loss.win_rate_pct === null ? '—' : `${data.win_loss.win_rate_pct}%`
              }
            />
            <Kpi
              label={t('avgWinDays')}
              value={
                data.win_loss.avg_win_days === null
                  ? '—'
                  : t('nDays', { n: data.win_loss.avg_win_days })
              }
            />
            <Kpi label={t('dealsWon')} value={data.win_loss.won} />
            <Kpi label={t('dealsLost')} value={data.win_loss.lost} />
          </div>

          <div className="grid-auto">
            <Funnel rows={data.funnel} t={t} />

            {/* ------------------------------------------------------ leaderboard */}
            <Card title={t('leaderboard')} style={{ gridColumn: '1/-1' }}>
              {data.leaderboard.length === 0 ? (
                <Empty>{t('noClosedYet')}</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('rep')}</th>
                        <th style={{ textAlign: 'right' }}>{t('wonCol')}</th>
                        <th style={{ textAlign: 'right' }}>{t('newCust')}</th>
                        <th style={{ textAlign: 'right' }}>{t('upsell')}</th>
                        <th style={{ textAlign: 'right' }}>{t('attainment')}</th>
                        <th style={{ textAlign: 'right' }}>{t('winRate')}</th>
                        <th style={{ textAlign: 'right' }}>{t('callsLogged')}</th>
                        <th style={{ textAlign: 'right' }}>{t('briefsIn')}</th>
                        <th style={{ textAlign: 'right' }}>{t('vsPrevShort')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.leaderboard.map((r) => (
                        <tr key={r.user_id}>
                          <td>
                            <span
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
                            >
                              <Avatar
                                initials={r.avatar_initials}
                                seed={r.avatar_var}
                                size={22}
                              />
                              <span>
                                <span style={{ display: 'block', fontWeight: 600 }}>{r.name}</span>
                                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                                  {r.role}
                                </span>
                              </span>
                            </span>
                          </td>
                          <td className="num" style={{ textAlign: 'right', fontWeight: 700 }}>
                            {thbShort(r.won_satang)}
                          </td>
                          <td className="num" style={{ textAlign: 'right' }}>
                            {thbShort(r.won_new_satang)}
                          </td>
                          <td className="num" style={{ textAlign: 'right' }}>
                            {thbShort(r.won_upsell_satang)}
                          </td>
                          <td
                            className="num"
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color:
                                r.attainment_pct === null
                                  ? 'var(--ink-3)'
                                  : r.attainment_pct >= 100
                                    ? 'var(--success)'
                                    : r.attainment_pct >= 60
                                      ? 'var(--ink-2)'
                                      : 'var(--warning)',
                            }}
                          >
                            {/* null means nobody set a quota — different from 0%. */}
                            {r.attainment_pct === null ? '—' : `${Math.round(r.attainment_pct)}%`}
                          </td>
                          <td className="num" style={{ textAlign: 'right' }}>
                            {r.win_rate_pct === null ? '—' : `${r.win_rate_pct}%`}
                          </td>
                          <td className="num" style={{ textAlign: 'right' }}>
                            {r.calls}
                          </td>
                          <td className="num" style={{ textAlign: 'right' }}>
                            {r.briefs}
                          </td>
                          <td
                            className="num"
                            style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color:
                                r.change_pct === null
                                  ? 'var(--ink-3)'
                                  : r.change_pct >= 0
                                    ? 'var(--success)'
                                    : 'var(--danger)',
                            }}
                          >
                            {r.change_pct === null
                              ? '—'
                              : `${r.change_pct >= 0 ? '+' : ''}${r.change_pct}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* --------------------------------------------------------- win/loss */}
            <Card title={t('lossReasons')}>
              {data.win_loss.reasons.length === 0 ? (
                <Empty>{t('noLosses')}</Empty>
              ) : (
                data.win_loss.reasons.map((r, i) => (
                  <div className="row" key={`${r.reason}-${r.competitor}-${i}`}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="row-title" style={{ display: 'block' }}>
                        {r.reason}
                      </span>
                      {r.competitor !== '—' && (
                        <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {t('lostTo')} {r.competitor}
                        </span>
                      )}
                    </span>
                    <span className="num" style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                      {thbShort(r.value_satang)}
                    </span>
                    <span
                      className="num"
                      style={{ width: 28, textAlign: 'right', fontWeight: 700 }}
                    >
                      {r.n}
                    </span>
                  </div>
                ))
              )}
            </Card>

            {/* ------------------------------------------------------------- gaps */}
            <Card title={t('unassigned')}>
              {/* Fixed 2×2 rather than auto-fit: three of these labels wrap to
                  two lines and one does not, so an auto-fit row leaves the last
                  count stranded under a ragged edge. */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 14,
                  padding: '6px 2px',
                }}
              >
                <GapCount label={t('accountsNoAe')} n={data.gaps.accounts_no_ae} />
                <GapCount label={t('dealsNoOwner')} n={data.gaps.deals_no_owner} />
                <GapCount label={t('prospectsNoOwner')} n={data.gaps.prospects_no_owner} />
                <GapCount
                  label={t('noNextAction')}
                  n={data.gaps.accounts_without_next_action}
                />
              </div>
            </Card>

            <Card title={t('idleDeals')}>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '0 2px 8px' }}>
                {t('coldAfter', { n: data.thresholds.cold_days })}
              </div>
              {data.gaps.idle_deals.length === 0 ? (
                <Empty>{t('nothingIdle')}</Empty>
              ) : (
                data.gaps.idle_deals.map((d) => (
                  <div className="row row-click" key={d.id} onClick={() => onOpenDeal(d.id)}>
                    <EntityGlyph entity={d.entity_tag} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="row-title" style={{ display: 'block' }}>
                        {d.title}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                        {d.company_name} · {d.stage} · {d.owner_name || t('unassignedOne')}
                      </span>
                    </span>
                    <span className="num" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                      {thbShort(d.amount_satang)}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: d.idle_days > 21 ? 'var(--danger)' : 'var(--warning)',
                        width: 46,
                        textAlign: 'right',
                      }}
                    >
                      {t('idle', { n: d.idle_days })}
                    </span>
                  </div>
                ))
              )}
            </Card>

            <Card title={t('untouchedAccounts')}>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '0 2px 8px' }}>
                {t('untouchedFor', { n: data.thresholds.account_touch_days })}
              </div>
              {data.gaps.untouched_accounts.length === 0 ? (
                <Empty>{t('allTouched')}</Empty>
              ) : (
                data.gaps.untouched_accounts.map((c) => (
                  <div className="row row-click" key={c.id} onClick={() => onOpenClient(c.id)}>
                    <EntityGlyph entity={c.entity_tag} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="row-title" style={{ display: 'block' }}>
                        {c.name}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{c.code}</span>
                    </span>
                    <span
                      style={{ fontSize: 11, fontWeight: 700, color: 'var(--warning)' }}
                    >
                      {c.idle_days}d
                    </span>
                  </div>
                ))
              )}
            </Card>

            <Card title={t('ungraduatedWins')}>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '0 2px 8px' }}>
                {t('ungraduatedBlurb')}
              </div>
              {data.gaps.ungraduated_wins.length === 0 ? (
                <Empty>{t('allGraduated')}</Empty>
              ) : (
                data.gaps.ungraduated_wins.map((d) => (
                  <div className="row row-click" key={d.id} onClick={() => onOpenDeal(d.id)}>
                    <EntityGlyph entity={d.entity_tag} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="row-title" style={{ display: 'block' }}>
                        {d.company_name || d.title}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                        {t('wonOn')} {shortDate(d.closed_at, lang)} · {d.owner_name || '—'}
                      </span>
                    </span>
                    <span className="num" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                      {thbShort(d.amount_satang)}
                    </span>
                  </div>
                ))
              )}
            </Card>
          </div>

          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 16 }}>
            {t('generatedAt', { d: data.generated_at })} · {periodLabel(data.period, lang)} ·{' '}
            {data.entities.join(' + ')}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Stage funnel, one block per entity.
 *
 * `rollup_funnel` has no `kind` column, so this counts new-business deals only —
 * folding the upsell board in would mix two stage vocabularies against a single
 * denominator and every conversion would read null. Said on the card rather
 * than left for someone to discover.
 */
function Funnel({ rows, t }) {
  const byEntity = new Map();
  for (const r of rows) {
    if (!byEntity.has(r.entity)) byEntity.set(r.entity, []);
    byEntity.get(r.entity).push(r);
  }

  return (
    <Card title={t('funnelTitle')} style={{ gridColumn: '1/-1' }}>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', padding: '0 2px 10px' }}>
        {t('funnelNewOnly')}
      </div>
      {rows.length === 0 ? (
        <Empty>{t('noFunnelData')}</Empty>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(byEntity.size, 2)}, minmax(0, 1fr))`,
            gap: 22,
          }}
        >
          {[...byEntity.entries()].map(([entity, stages]) => {
            const max = Math.max(...stages.map((s) => Number(s.count) || 0), 1);
            return (
              <div key={entity}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    fontWeight: 700,
                    fontSize: 12.5,
                    marginBottom: 10,
                  }}
                >
                  <EntityGlyph entity={entity} />
                  {entity}
                </div>
                {stages.map((s) => (
                  <div key={s.stage} style={{ marginBottom: 9 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 8,
                        fontSize: 12,
                        marginBottom: 3,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{s.stage}</span>
                      <span className="num" style={{ color: 'var(--ink-3)' }}>
                        {thbShort(s.value_satang)}
                      </span>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                        {s.conv_pct !== null && s.conv_pct !== undefined && (
                          <span style={{ color: 'var(--ink-3)', fontSize: 11.5 }}>
                            {Math.round(s.conv_pct)}%
                          </span>
                        )}
                        <strong className="num">{s.count}</strong>
                      </span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 4,
                        background: 'var(--sunken)',
                        overflow: 'hidden',
                      }}
                    >
                      <span
                        style={{
                          display: 'block',
                          height: '100%',
                          width: `${((Number(s.count) || 0) / max) * 100}%`,
                          background:
                            entity === 'BTM' ? 'var(--entity-btm)' : 'var(--entity-mr)',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

const GapCount = ({ label, n }) => (
  <div>
    <div
      className="display num"
      style={{ fontSize: 24, lineHeight: 1.1, color: n > 0 ? 'var(--warning)' : 'var(--ink-3)' }}
    >
      {n ?? 0}
    </div>
    <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>{label}</div>
  </div>
);
